import React, { useCallback, useMemo, useRef, useState } from 'react';
import { lookupApi } from './lookupApi';
import './lookup.css';

const LOOKUP_FIELDS = [
  { key: 'code', label: 'Code', required: true, defaultable: false },
  { key: 'description', label: 'Description', required: true, defaultable: false },
  { key: 'title', label: 'Title', required: false, defaultable: false },
  { key: 'category', label: 'Category', required: false, defaultable: true },
  { key: 'source', label: 'Source', required: false, defaultable: true },
  { key: 'version', label: 'Version', required: false, defaultable: true },
  { key: 'effectiveDate', label: 'Effective Date', required: false, defaultable: true },
  { key: 'relatedRules', label: 'Related Rules', required: false, defaultable: false },
  { key: 'graphTriggers', label: 'Graph Triggers', required: false, defaultable: false },
  { key: 'denialRelevance', label: 'Denial Relevance', required: false, defaultable: false },
  { key: 'clinicalEvidenceNeeded', label: 'Clinical Evidence Needed', required: false, defaultable: false },
  { key: 'codingEvidenceNeeded', label: 'Coding Evidence Needed', required: false, defaultable: false },
  { key: 'appealLogicHints', label: 'Appeal Logic Hints', required: false, defaultable: false },
];

const DELIMITER_OPTIONS = [
  { value: 'tab', label: 'Tab' },
  { value: 'comma', label: 'Comma (,)' },
  { value: 'pipe', label: 'Pipe (|)' },
  { value: 'semicolon', label: 'Semicolon (;)' },
  { value: 'whitespace', label: 'Whitespace (2+ spaces or tab)' },
  { value: 'auto', label: 'Auto-detect' },
];

const ACCEPTED_EXTENSIONS = ['.txt', '.csv', '.tsv'];

const DEFAULT_MAPPING = LOOKUP_FIELDS.reduce((acc, f) => { acc[f.key] = ''; return acc; }, {});
DEFAULT_MAPPING.code = '1';
DEFAULT_MAPPING.description = '2';

/**
 * Scope-aware CSV/TXT lookup upload (preview + commit).
 * Props: caseId (null for account scope), onCommitted (callback after success).
 */
export default function LookupUpload({ caseId = null, onCommitted }) {
  const scope = useMemo(() => ({ caseId: caseId || undefined }), [caseId]);
  const fileInputRef = useRef(null);

  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [delimiter, setDelimiter] = useState('tab');
  const [skipFirstLine, setSkipFirstLine] = useState(false);
  const [defaults, setDefaults] = useState({ category: '', source: '', version: '', effectiveDate: '' });
  const [mapping, setMapping] = useState(DEFAULT_MAPPING);

  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [commitResult, setCommitResult] = useState(null);

  const mappingForApi = useMemo(() => {
    const out = {};
    Object.entries(mapping).forEach(([key, raw]) => {
      const value = String(raw ?? '').trim();
      if (!value) return;
      const n = Number(value);
      if (Number.isFinite(n) && n >= 1) out[key] = n - 1;
    });
    return out;
  }, [mapping]);

  const mappedFieldsForPreview = useMemo(
    () => LOOKUP_FIELDS.filter((f) => mappingForApi[f.key] !== undefined || (f.defaultable && defaults[f.key])),
    [mappingForApi, defaults],
  );

  const validateFile = (candidate) => {
    if (!candidate) return false;
    const ext = candidate.name.includes('.') ? `.${candidate.name.split('.').pop().toLowerCase()}` : '';
    if (ext && !ACCEPTED_EXTENSIONS.includes(ext)) {
      setError(`Unsupported file type ${ext}. Allowed: ${ACCEPTED_EXTENSIONS.join(', ')}`);
      return false;
    }
    if (candidate.size > 25 * 1024 * 1024) {
      setError('File exceeds 25 MB limit');
      return false;
    }
    return true;
  };

  const handleFileSelected = (candidate) => {
    setError(null);
    setPreview(null);
    setCommitResult(null);
    if (!validateFile(candidate)) { setFile(null); return; }
    setFile(candidate);
  };

  const handleFileInputChange = (e) => {
    const next = e.target.files?.[0];
    if (next) handleFileSelected(next);
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const next = e.dataTransfer.files?.[0];
    if (next) handleFileSelected(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMappingChange = (key) => (e) => setMapping((prev) => ({ ...prev, [key]: e.target.value }));
  const handleDefaultChange = (key) => (e) => setDefaults((prev) => ({ ...prev, [key]: e.target.value }));

  const buildOptions = () => ({
    delimiter,
    skipFirstLine,
    fieldMapping: mappingForApi,
    defaults: Object.fromEntries(Object.entries(defaults).filter(([, v]) => String(v).trim() !== '')),
    sampleSize: 50,
  });

  const ensureReady = () => {
    if (!file) { setError('Please choose a file'); return false; }
    if (mappingForApi.code === undefined) { setError("Field mapping must include 'Code'"); return false; }
    if (mappingForApi.description === undefined) { setError("Field mapping must include 'Description'"); return false; }
    return true;
  };

  const runPreview = async () => {
    if (!ensureReady()) return;
    setPreviewing(true);
    setError(null);
    setSuccess(null);
    setCommitResult(null);
    try {
      const resp = await lookupApi.previewUpload(file, buildOptions(), scope);
      if (resp?.success) setPreview(resp.data);
      else setError(resp?.message || 'Preview failed');
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  const runCommit = async () => {
    if (!ensureReady()) return;
    if (!preview) { setError('Run Preview first to confirm the parsed rows look correct'); return; }
    setCommitting(true);
    setError(null);
    setSuccess(null);
    try {
      const resp = await lookupApi.commitUpload(file, buildOptions(), scope);
      if (resp?.success) {
        setCommitResult(resp.data);
        setSuccess(resp.message || 'Upload completed');
        if (onCommitted) onCommitted(resp.data);
      } else {
        setError(resp?.message || 'Upload failed');
      }
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Upload failed');
    } finally {
      setCommitting(false);
    }
  };

  return (
    <div className="lookup-upload">
      <div className="lookup-upload__section">
        <h2>1. Choose file</h2>
        <div
          className={`lookup-dropzone ${dragActive ? 'lookup-dropzone--active' : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInputRef.current?.click(); } }}
        >
          <input ref={fileInputRef} type="file" accept={ACCEPTED_EXTENSIONS.join(',')} onChange={handleFileInputChange} style={{ display: 'none' }} />
          <div><strong>Click to upload</strong> or drag and drop</div>
          <div className="lookup-upload__sub" style={{ margin: '6px 0 0 0' }}>.txt, .csv, .tsv up to 25 MB</div>
          {file && (
            <div className="lookup-dropzone__file">
              Selected: <strong>{file.name}</strong> ({Math.round(file.size / 1024)} KB)
            </div>
          )}
        </div>
      </div>

      <div className="lookup-upload__section">
        <h2>2. Parse options</h2>
        <div className="lookup-options">
          <div>
            <label htmlFor="lu-delimiter">Delimiter</label>
            <select id="lu-delimiter" value={delimiter} onChange={(e) => setDelimiter(e.target.value)}>
              {DELIMITER_OPTIONS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
            </select>
          </div>
          <div className="lookup-options__checkbox">
            <input id="lu-skip-first" type="checkbox" checked={skipFirstLine} onChange={(e) => setSkipFirstLine(e.target.checked)} />
            <label htmlFor="lu-skip-first" style={{ margin: 0 }}>Ignore first line (header row)</label>
          </div>
          <div>
            <label htmlFor="lu-def-category">Default Category</label>
            <input id="lu-def-category" type="text" placeholder="(applied to rows without one)" value={defaults.category} onChange={handleDefaultChange('category')} />
          </div>
          <div>
            <label htmlFor="lu-def-source">Default Source</label>
            <input id="lu-def-source" type="text" placeholder="e.g. CMS MS-DRG v43" value={defaults.source} onChange={handleDefaultChange('source')} />
          </div>
          <div>
            <label htmlFor="lu-def-version">Default Version</label>
            <input id="lu-def-version" type="text" placeholder="e.g. v43.1" value={defaults.version} onChange={handleDefaultChange('version')} />
          </div>
          <div>
            <label htmlFor="lu-def-effective">Default Effective Date</label>
            <input id="lu-def-effective" type="date" value={defaults.effectiveDate} onChange={handleDefaultChange('effectiveDate')} />
          </div>
        </div>
      </div>

      <div className="lookup-upload__section">
        <h2>3. Map file columns to lookup fields</h2>
        <div className="lookup-mapping">
          <table>
            <thead>
              <tr>
                {LOOKUP_FIELDS.map((f) => (
                  <th key={f.key} className={f.required ? 'req' : ''} title={f.required ? 'Required' : 'Optional'}>{f.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                {LOOKUP_FIELDS.map((f) => (
                  <td key={f.key}>
                    <input type="number" min="1" step="1" placeholder="—" value={mapping[f.key]} onChange={handleMappingChange(f.key)} title={`Column number in the file containing ${f.label} (1-based)`} />
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
          <p className="lookup-mapping__hint">
            Enter the 1-based column number from the file under each field. Leave blank if the file does not
            have that column. Code and Description are required.
          </p>
        </div>
      </div>

      {error && <div className="lookup-banner lookup-banner--error">{error}</div>}
      {success && <div className="lookup-banner lookup-banner--success">{success}</div>}

      <div className="lookup-upload__section">
        <h2>4. Preview</h2>
        <p className="lookup-upload__sub">
          Parses the file with the options above and shows the first 50 matched records, plus a count of
          skipped lines (blank rows, TOC text, headers, lines without a valid code+description). Nothing is
          inserted at this step.
        </p>
        <div className="lookup-upload__footer">
          <button type="button" className="lookup-btn lookup-btn--primary" onClick={runPreview} disabled={previewing || !file}>
            {previewing ? 'Parsing…' : 'Preview File'}
          </button>
        </div>

        {preview && (
          <>
            <div className="lookup-stats" style={{ marginTop: 12 }}>
              <span className="lookup-stats__chip lookup-stats__chip--ok">Parsed: {preview.parsed.toLocaleString()}</span>
              <span className="lookup-stats__chip lookup-stats__chip--warn">Skipped: {preview.skipped.toLocaleString()}</span>
              <span className="lookup-stats__chip">Total lines: {preview.totalLines.toLocaleString()}</span>
              <span className="lookup-stats__chip">Delimiter: {preview.detectedDelimiter}</span>
            </div>

            {preview.sample?.length > 0 ? (
              <div className="lookup-preview">
                <table>
                  <thead>
                    <tr>{mappedFieldsForPreview.map((f) => <th key={f.key}>{f.label}</th>)}</tr>
                  </thead>
                  <tbody>
                    {preview.sample.map((row, idx) => (
                      <tr key={idx}>
                        {mappedFieldsForPreview.map((f) => (
                          <td key={f.key} title={row[f.key] || ''}>{String(row[f.key] || '').slice(0, 120)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="lookup-banner lookup-banner--info">
                No records matched the mapping. Adjust the delimiter / column indexes and try again.
              </div>
            )}
          </>
        )}
      </div>

      <div className="lookup-upload__section">
        <h2>5. Commit</h2>
        <p className="lookup-upload__sub">
          Inserts parsed rows into {caseId ? "this case's" : "the account's"} lookup table. Duplicate rows
          (same code and category that already exist in this scope) are silently skipped.
        </p>
        <div className="lookup-upload__footer">
          <button type="button" className="lookup-btn lookup-btn--primary" onClick={runCommit} disabled={committing || !preview || preview.parsed === 0}>
            {committing ? 'Uploading…' : 'Commit Upload'}
          </button>
        </div>

        {commitResult && (
          <div className="lookup-stats" style={{ marginTop: 12 }}>
            <span className="lookup-stats__chip lookup-stats__chip--ok">Inserted: {commitResult.inserted.toLocaleString()}</span>
            <span className="lookup-stats__chip lookup-stats__chip--warn">Skipped duplicates: {commitResult.skippedDuplicates.toLocaleString()}</span>
            <span className="lookup-stats__chip">Parsed rows submitted: {commitResult.submitted.toLocaleString()}</span>
            <span className="lookup-stats__chip">Lines skipped by parser: {commitResult.skippedLines.toLocaleString()}</span>
          </div>
        )}
      </div>
    </div>
  );
}
